import styled from 'styled-components';

import { InputWrapper } from '@/core/components/InputWrapper';

import { fieldDescribedBy } from '@/utils/fieldIds';

interface FormSelectProps {
  backgroundcolor: string;
  error: () => string | false;
  /**
   * Optional description of why the field exists or how it is used.
   * Always exposed to screen readers via aria-describedby; visually
   * revealed on demand through a small help toggle inside the field.
   */
  helpText?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onFocus: () => void;
  /**
   * Optional callback invoked when the browser fires the `invalid` event on the select.
   * Called after the native tooltip is suppressed via `event.preventDefault()`.
   * Use this to set a styled inline error message in place of the browser's native tooltip.
   */
  onInvalidField?: () => void;
  name: string;
  options: Array<string>;
  value: string | undefined;
}

export const Select = styled.select`
  background-color: #fff !important;
  color: ${props => (props.value ? 'black' : 'rgba(0, 0, 0, 0.7)')};
`;

export const FormSelect = ({
  backgroundcolor,
  error,
  helpText,
  isDisabled,
  isRequired,
  onChange,
  onFocus,
  onInvalidField,
  options,
  name,
  value,
}: FormSelectProps) => {
  const describedBy = fieldDescribedBy({
    fieldName: name,
    hasError: !!error(),
    hasHelp: !!helpText,
  });

  const handleInvalid = (event: React.InvalidEvent<HTMLSelectElement>) => {
    if (onInvalidField) {
      event.preventDefault();
      onInvalidField();
    }

    const select = event.target;

    if (select.validity.valueMissing) {
      select.setCustomValidity(`Please select a ${name.toLowerCase()} option`);
    } else {
      select.setCustomValidity('');
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    // Clear custom validity message when user makes a selection
    event.target.setCustomValidity('');
    onChange(event);
  };

  return (
    <InputWrapper
      backgroundcolor={backgroundcolor}
      error={error}
      fieldName={name}
      helpText={helpText}
      isRequired={isRequired}
      isSelect
    >
      <Select
        aria-describedby={describedBy}
        aria-invalid={!!error()}
        aria-label={name}
        data-has-value={value || undefined}
        disabled={isDisabled}
        name={name}
        onChange={handleChange}
        onFocus={() => onFocus()}
        onInvalid={handleInvalid}
        required={isRequired}
        value={value}
      >
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <option
          value=""
          disabled
          hidden
        />
        {options?.map(option => (
          <option
            key={option}
            value={option}
          >
            {option}
          </option>
        ))}
      </Select>
      <span>{name}</span>
    </InputWrapper>
  );
};

FormSelect.defaultProps = {
  helpText: '',
  isDisabled: false,
  isRequired: false,
};
